import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useEffect, useState, useContext, useRef, Fragment, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatMessage, { Message } from './ChatMessage';
import { PromptContext } from '../../context/PromptContext';
import { LoadingContext } from '../../context/LoadingContext';
import { SettingContext } from '../../context/SettingContext';
import { AuthContext } from '@/context/AuthContext';

import { getLeadProfile } from '../../services/profile';

import { useHistoryFunctions } from '../../providers/historyProvider';
import { encryptAesWithSha256, formatInput, formatMarkdown } from '../../utils/chat';
import {
  CHAT_DISABLED_ID,
  ELYSIA_GUARDRAIL_ERROR_MESSAGE,
  ELYSIA_INTERNAL_ERROR_MESSAGE,
} from '../../config/settings';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import useElysiaStore from '@/app/elysia-store';
import { getBrandDetails } from '@/services/brand';
import { authenticatedFetch } from '@/utils/authenticatedFetch';
import useCitation from '@/hooks/useCitation';

// Initialize the variables to handle api abort, cancel streaming
let cancelStreaming = false;
let isStreaming = false;
let controller = new AbortController();
let signal = controller.signal;
let metadataSourceList = [];
const toolCallKey = 'toolCall';
let isElysiaError = false;
let isImageGenChat = false;

// Thinking and SQL tracking variables
let currentSectionIndex = 0;
let isInThinkingBlock = false;
let hasSeenTextChunk = false;
let currentToolInput = '';
let currentResponseBlocks: ResponseBlock[] = [];
let isNewFormatThinking = false;
let hasChartBeenSeen = false;
let lastThinkingClosed = false;

const ChatSession = ({ contentRef }) => {
  const division = useElysiaStore((state) => state.division);
  const persona = useElysiaStore((state) => state.persona);
  const brand = useElysiaStore((state) => state.brand);
  const brandDetails = useElysiaStore((state) => state.brandDetails);
  const isPreview = useElysiaStore((state) => state.isPreview);
  const privewApiPayload = useElysiaStore((state) => state.privewApiPayload);
  const allDivisions = useElysiaStore((state) => state.allDivisions);
  const brandDoc = useElysiaStore((state) => state.brandDoc);
  const appPreviewApiPayload = useElysiaStore((state) => state.appPreviewApiPayload);
  const isAppPreview = useElysiaStore((state) => state.isAppPreview);
  const aiAppId = useElysiaStore((state) => state.aiAppId);
  const imageGenPayload = useElysiaStore((state) => state.imageGenPayload);
  const imageLoading = useElysiaStore((state) => state.imageLoading);
  const activeChatHistoryRecord = useElysiaStore((state) => state.activeChatHistoryRecord);
  const chatSessionId = useElysiaStore((state) => state.chatSessionId);
  const newChat = useElysiaStore((state) => state.newChat);
  const clearState = useElysiaStore((state) => state.clearState);
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const context = useElysiaStore((state) => state.context);
  const contextName = useElysiaStore((state) => state.contextName);
  const sources = useElysiaStore((state) => state.sources);
  const nextBestActions = useElysiaStore((state) => state.nextBestActions);
  const entryPoint = useElysiaStore((state) => state.entryPoint);
  const includeSearch = useElysiaStore((state) => state.includeSearch);
  const isChatActive = useElysiaStore((state) => state.isChatActive);
  const isPrivateChat = useElysiaStore((state) => state.isPrivateChat);
  const isStreamingStopped = useElysiaStore.getState().isStreamingStopped;
  const localToolResultKey = useElysiaStore((state) => state.localToolResultKey) || 'toolResult';
  const localAutoScrollEnabledKey =
    useElysiaStore((state) => state.localAutoScrollEnabledKey) || 'autoScrollEnabled';
  const availablePrivateDocs = useElysiaStore((state) => state.availablePrivateDocs);
  const historyCollectionName = useElysiaStore((state) => state.historyCollectionName);
  const dataAgentDocs = useElysiaStore((state) => state.dataAgentDocs);
  const profitCenter = useElysiaStore((state) => state.profitCenter);
  const financeAppLocation = useElysiaStore((state) => state.financeAppLocation);
  const appKey = useElysiaStore((state) => state.appKey);

  const setDivision = useElysiaStore.getState().setDivision;
  const setBrand = useElysiaStore.getState().setBrand;
  const setPersona = useElysiaStore.getState().setPersona;
  const setBrandDetails = useElysiaStore.getState().setBrandDetails;
  const setAiAppId = useElysiaStore.getState().setAiAppId;
  const setUploadedImageId = useElysiaStore.getState().setUploadedImageId;
  const setImageGenPayload = useElysiaStore.getState().setImageGenPayload;
  const setImageLoading = useElysiaStore.getState().setImageLoading;
  const setIsStreamingRequested = useElysiaStore.getState().setIsStreamingRequested;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setClearState = useElysiaStore.getState().setClearState;
  const setIsNewSession = useElysiaStore.getState().setIsNewSession;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setBackToChatSession = useElysiaStore.getState().setBackToChatSession;
  const setIsChatActive = useElysiaStore.getState().setIsChatActive;
  const setIsChatDisabled = useElysiaStore.getState().setIsChatDisabled;
  const setDynamicStreamCount = useElysiaStore.getState().setDynamicStreamCount;
  const setFinanceAppLocation = useElysiaStore.getState().setFinanceAppLocation;
  const setProfitCenter = useElysiaStore.getState().setProfitCenter;
  const pendingMessageFiles = useElysiaStore.getState().pendingMessageFiles || [];
  const setPendingMessageFiles = useElysiaStore.getState().setPendingMessageFiles;

  const navigate = useNavigate();
  const { prompt, setPrompt, isProcessed, setProcessed } = useContext(PromptContext);
  const { profileInfo } = useContext(AuthContext);
  const { setIsLoading } = useContext(LoadingContext);
  const activeChatHistoryConversation = activeChatHistoryRecord?.history || [];
  const historyFunctions = useHistoryFunctions();
  const [isStreamCompleted, setIsStreamCompleted] = useState<boolean>(false);
  const [responseImageId, setResponseImageId] = useState<string>('');
  const [pptConfig, setPptConfig] = useState({});
  const location = useLocation();
  const {
    defaultLanguage,
    defaultModel,
    selectedLanguage,
    selectedModel,
    languageUsedForResponse,
    modelUsedForResponse,
    setAvailableImageQuota,
  } = useContext(SettingContext);

  // citation
  const {
    citations,
    registerCitationEvent,
    extractCitationsFromRawBlocks,
    resetCitations,
    getCurrentCitations,
  } = useCitation();

  // Thinking and SQL state
  const [sqlSections, setSqlSections] = useState<any[]>([]);

  // New: State for interleaved thinking/text blocks
  const [responseBlocks, setResponseBlocks] = useState<ResponseBlock[]>([]);

  if (!historyFunctions) {
    throw new Error('History functions are not available.');
  }
  const { addChatToToday, updateSessionTitle, updateChatHistoryOnDelete } = historyFunctions;
  const {
    modelProvider,
    modelName,
    personality,
    creativity,
    role,
    domainExpertise,
    writingStyle,
    outputLanguage,
    showSourceList,
  } = useContext(SettingContext);
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [result, setResult] = useState<string | null>(null);
  const [toolCall, setToolCall] = useState('');
  const [toolResult, setToolResults] = useState<any[]>([]);
  const [chartConfig, setChartConfig] = useState<any | null>(null);
  const { allModels, setSelectedLanguage, setSelectedModel } = useContext(SettingContext);
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const resultScrollRef = useRef<HTMLDivElement>(null);
  const [runId, setRunId] = useState<string>('');
  const models = useRef({});
  const networkDoneRef = useRef(false);
  const COMMIT_INTERVAL_MS = 45;
  const lastCommitTsRef = useRef(0);
  const pendingChangedBlocksRef = useRef<Set<number>>(new Set());
  const pendingResultDeltaRef = useRef('');
  const lastAutoScrollTopRef = useRef<number | null>(null);

  //if any of the setting context changes - get notified, but do nothing immediately
  useEffect(() => {
    //do nothing immediately
  }, [
    modelProvider,
    modelName,
    personality,
    creativity,
    role,
    domainExpertise,
    writingStyle,
    outputLanguage,
    showSourceList,
    includeSearch,
  ]);

  const combinedMessages = useMemo(() => {
    if (!isChatActive) return chatMessages;

    if (isStreaming && (result || toolCall || responseBlocks.length > 0)) {
      return [
        ...chatMessages,
        {
          type: 'System',
          body: result,
          toolCall,
          toolResult,
          imageId: responseImageId,
          chartConfig,
          imageLoading,
          responseBlocks: responseBlocks,
          sqlSections: sqlSections,
          isElysiaError: isElysiaError,
          isStreaming: true,
          citations: citations,
        } as Message,
      ];
    }
    return chatMessages;
  }, [
    chatMessages,
    isChatActive,
    result,
    toolCall,
    toolResult,
    responseImageId,
    chartConfig,
    imageLoading,
    responseBlocks,
    sqlSections,
    citations,
  ]);

  //if agent changes, clear state and start over again
  useEffect(() => {
    setPrompt?.('');
    if (clearState || isStreamingStopped === true) {
      cancelStreaming = true;
      // abort the api when streaming isn't started yet
      if (!isStreaming) {
        controller.abort();
        setIsStreamingRequested?.(false);
      }
      if (!isStreamingStopped) {
        setChatMessages([]);
      }
    }
    // eslint-disable-next-line
  }, [
    setPrompt,
    clearState,
    chatAgent,
    context,
    contextName,
    nextBestActions,
    activeChatHistoryRecord,
    isStreamingStopped,
  ]);

  //if prompt from text input changes, react based on the chosen agent (in context)
  useEffect(() => {
    if (prompt && prompt !== '' && !isProcessed) {
      if (prompt !== '' && !prompt.startsWith('auto-trigger-prompt')) {
        const attachedFiles = useElysiaStore.getState().pendingMessageFiles || [];
        const newMessage: Message = {
          type: 'User',
          body: prompt,
          attachedFiles: attachedFiles,
        };
        setChatMessages((prevArray) => [...prevArray, newMessage]);
        setIsChatActive?.(true);
      }
      processPrompt();
      setProcessed?.(true);
    }
    //eslint-disable-next-line
  }, [prompt, isProcessed, setProcessed, setIsLoading]);

  //if chat history is updated in the current chat session
  useEffect(() => {
    if (
      (chatMessages.length || activeChatHistoryRecord?.session_id) &&
      sessionStorage.getItem(localAutoScrollEnabledKey) === 'true'
    ) {
      const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
      if (isMobile) {
        setTimeout(() => {
          scrollRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'start',
          });
        }, 50);
      } else {
        // Desktop scrolling
        scrollRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'start',
        });
      }
    }
  }, [chatMessages.length, activeChatHistoryRecord?.session_id, localAutoScrollEnabledKey]);

  // when history load land at bottom of the chat in virtuoso
  useEffect(() => {
    if (
      activeChatHistoryRecord?.session_id &&
      sessionStorage.getItem(localAutoScrollEnabledKey) === 'true'
    ) {
      const delay = /iPhone|iPad|Android/i.test(navigator.userAgent) ? 300 : 250;
      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
      }, delay);
    }
    // eslint-disable-next-line
  }, [activeChatHistoryRecord?.session_id, activeChatHistoryConversation.length]);

  // abort/cancel the streaming api on component unmount
  useEffect(() => {
    return () => {
      cancelStreaming = true;
      if (!isStreaming) {
        controller.abort();
      }
      setIsChatActive?.(false);
      setIsStreamingRequested?.(false);
      setChatMessages([]);
    };
    // eslint-disable-next-line
  }, [setIsStreamingRequested]);

  // If private chat mode changes, clear chat messages
  useEffect(() => {
    setChatMessages([]);
  }, [isPrivateChat]);

  useEffect(() => {
    if (isStreamCompleted) {
      appendMesgToChatHistory(
        result,
        runId,
        localStorage.getItem(localToolResultKey),
        localStorage.getItem(toolCallKey)
      );
    }
    // eslint-disable-next-line
  }, [result, isStreamCompleted, runId]);
  // effect to clear the state when streaming is stopped
  useEffect(
    () => {
      if (isStreamingStopped === true && !imageLoading) {
        setIsLoading(false);
      }
    },
    //eslint-disable-next-line
    [isStreamingStopped, setIsLoading]
  );

  useEffect(
    () => {
      const documentsInChatSession = activeChatHistoryConversation[0]?.references?.map((doc) =>
        doc?.title.toString()
      );
      const agentName = activeChatHistoryRecord?.metadata?.agent_name;
      if (dataAgentDocs && agentName === 'Data Agent') {
        const isDocumentDeleted = documentsInChatSession.some(
          (doc) => !dataAgentDocs?.includes(doc)
        );
        setIsChatDisabled?.(isDocumentDeleted);
      }
    }, //eslint-disable-next-line
    [dataAgentDocs]
  );

  // temparary code to support backend for older history wont work if citation + doc enabled
  useEffect(() => {
    const metadata = activeChatHistoryRecord?.metadata;

    const agentName = metadata?.agent_name;
    const showAttachment = metadata?.show_attachment;
    const chatAgent = metadata?.chat_agent;

    // Data Agent should never be disabled by this validation
    if (agentName === 'Data Agent') return;
    setIsChatDisabled?.(false);
    if (!historyCollectionName || !activeChatHistoryConversation?.length || !availablePrivateDocs)
      return;

    const isPrivateCollection = historyCollectionName.includes('private');

    if (!isPrivateCollection) return;

    let documentsInChatSession: string[] = [];

    if (showAttachment === true) {
      documentsInChatSession =
        activeChatHistoryConversation[0]?.references
          ?.map((doc) => doc?.title?.toString())
          .filter(Boolean) ?? [];
    } else if (showAttachment === false && chatAgent === 'Document') {
      documentsInChatSession =
        metadata?.document_source?.map((doc) => doc?.toString()).filter(Boolean) ?? [];
    }

    if (!documentsInChatSession.length) return;

    const isDocumentDeleted = documentsInChatSession.some(
      (doc) => !availablePrivateDocs.includes(doc)
    );

    setIsChatDisabled?.(isDocumentDeleted);

    // eslint-disable-next-line
  }, [
    activeChatHistoryConversation,
    activeChatHistoryRecord,
    availablePrivateDocs,
    historyCollectionName,
  ]);

  useEffect(() => {
    toast.dismiss(CHAT_DISABLED_ID);
  }, [location]);

  useEffect(() => {
    allModels.forEach((model) => {
      models.current[`${model.name_of_model}`] = model.display_name;
    });
  }, [allModels]);

  useEffect(() => {
    if (
      activeChatHistoryRecord?.metadata?.response_language &&
      activeChatHistoryRecord?.metadata?.name_of_model
    ) {
      setSelectedLanguage(activeChatHistoryRecord?.metadata?.response_language);
      const responseModel = allModels.find(
        (model) => model.name_of_model === activeChatHistoryRecord?.metadata?.name_of_model
      );
      if (responseModel) {
        setSelectedModel(responseModel);
      } else {
        setSelectedModel(allModels[0]);
      }
    }
    //eslint-disable-next-line
  }, [activeChatHistoryRecord, allModels]);

  useEffect(() => {
    // checking if the history is a brand-agent and setting preferences
    const setBrandItems = async () => {
      const brandsForDiv = allDivisions.find(
        (div) => div.division === activeChatHistoryRecord?.metadata?.division
      );

      if (brandsForDiv && brandsForDiv?.brand?.length) {
        const selectedBrand = brandsForDiv.brand.find(
          (brand) => brand.name === activeChatHistoryRecord?.metadata?.brand
        );
        if (selectedBrand?.id?.length) {
          setBrand(selectedBrand);
          const brandDetails = await getBrandDetails(selectedBrand.id);
          setBrandDetails(brandDetails);
          const allPersonaDetails = brandDetails?.communication?.target_audiences;
          if (activeChatHistoryRecord?.metadata?.persona_id?.length) {
            const selectedPersona = allPersonaDetails.find(
              (persona) => persona.id === activeChatHistoryRecord?.metadata?.persona_id
            );
            setPersona(selectedPersona);
          }
        }
      }
    };

    const setImageGentItems = async () => {
      if (activeChatHistoryRecord?.metadata?.agent_metadata?.image_generation_agent) {
        const data = activeChatHistoryRecord?.metadata?.agent_metadata?.image_generation_agent;
        setImageGenPayload({
          aspect_ratio: data?.aspect_ratio,
          color_palette: data?.color_palette,
          // negative_prompt: data.negative_prompt,
          starter_image_id: data?.starter_image_id,
          image_quality: data?.image_quality,
          starter_image_file_name: data?.starter_image_file_name,
        });
        setUploadedImageId(data.starter_image_id ?? '');
      }
    };

    const initializeChatSession = async () => {
      if (activeChatHistoryRecord?.metadata?.agent_id?.length) {
        setAiAppId(activeChatHistoryRecord?.metadata?.agent_id);
        setDivision(activeChatHistoryRecord?.metadata?.division ?? '');
        await setBrandItems(); // Wait for the async function to complete
      }
      if (activeChatHistoryRecord?.metadata?.agent_metadata?.image_generation_agent) {
        await setImageGentItems();
      }

      if (
        activeChatHistoryRecord?.metadata?.profit_center ||
        activeChatHistoryRecord?.metadata?.location
      ) {
        const profitCenterData: ProfitCenter = {
          value: activeChatHistoryRecord?.metadata?.profit_center?.id || '',
          level: activeChatHistoryRecord?.metadata?.profit_center?.level || 0,
          label: activeChatHistoryRecord?.metadata?.profit_center?.id || '',
        };
        const LocationData = {
          value: activeChatHistoryRecord?.metadata?.location?.id || '',
          level: activeChatHistoryRecord?.metadata?.location?.level || 0,
          label: activeChatHistoryRecord?.metadata?.location?.id || '',
        };
        setProfitCenter(profitCenterData);
        setFinanceAppLocation(LocationData);
      }
    };

    initializeChatSession(); // Call the async initializer
    // add default model as dependency as it chnages selected model in provider

    //eslint-disable-next-line
  }, [activeChatHistoryRecord, allDivisions, defaultModel]);

  useEffect(() => {
    if (isPreview || isAppPreview) {
      setChatMessages([]);
    }
    //eslint-disable-next-line
  }, [brand, isAppPreview]);

  const appendMesgToChatHistory = (message, runId, srcList, searchTxt) => {
    const newMessage: Message = {
      type: 'System',
      body: message,
      showInteractions: runId && runId !== '' ? true : false,
      prompts:
        chatAgent === 'Sales' && entryPoint === 'upload' && nextBestActions ? nextBestActions : [],
      runId: runId,
      references: metadataSourceList,
      toolResult: srcList ? JSON.parse(srcList) : [],
      toolCall: searchTxt,
      modelUsed: modelUsedForResponse?.current?.display_name,
      languageUsed: languageUsedForResponse?.current,
      imageId: responseImageId,
      chartConfig: chartConfig,
      pptConfig: pptConfig,
      responseBlocks: responseBlocks,
      sqlSections: sqlSections,
      isElysiaError: isElysiaError,
      citations: getCurrentCitations(),
    };
    setResult('');
    setToolCall('');
    setToolResults([]);
    setIsLoading(false);
    setIsStreamCompleted(false);

    setResponseImageId('');
    setChartConfig(null);
    setPptConfig({});
    setResponseBlocks([]);
    currentResponseBlocks = [];
    metadataSourceList = [];
    localStorage.setItem(toolCallKey, '');
    localStorage.removeItem(localToolResultKey);
    setRunId('');
    isImageGenChat = false;
    resetCitations();

    // Clear chat messages list on abort
    if (cancelStreaming && isStreamingStopped !== true) {
      setChatMessages([]);
      setIsChatActive?.(false);
    } else {
      setChatMessages((prevArray) => [...prevArray, newMessage]);
      setIsChatActive?.(true);
    }
  };

  type TypewriterItem = {
    blockIndex: number;
    text: string;
    index: number;
    isVisibleText: boolean;
    atomic?: boolean;
    endsBlock?: boolean;
  };

  const typewriterQueueRef = useRef<TypewriterItem[]>([]);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const charAccumulatorRef = useRef(0);
  const currentRateRef = useRef(52);
  const lastScrollTsRef = useRef(0);

  const BASE_RATE = 65;
  const MAX_RATE = 3000;
  const CATCHUP_THRESHOLD = 150;
  const CATCHUP_FACTOR = 0.5;
  const TIME_CONSTANT = 0.25;

  const getTargetRate = (backlog: number, done: boolean) => {
    const threshold = done ? 0 : CATCHUP_THRESHOLD;
    const excess = Math.max(0, backlog - threshold);
    return Math.min(BASE_RATE + excess * CATCHUP_FACTOR, MAX_RATE);
  };

  const getBacklog = () => {
    return typewriterQueueRef.current.reduce(
      (total, item) => total + item.text.length - item.index,
      0
    );
  };

  useEffect(() => {
    if (!toolCall && toolResult.length === 0) return;
    autoScroll(performance.now());
    // eslint-disable-next-line
  }, [toolCall, toolResult.length]);

  const autoScroll = (ts: number) => {
    if (sessionStorage.getItem(localAutoScrollEnabledKey) !== 'true') return;

    const el = contentRef.current;
    const last = lastAutoScrollTopRef.current;
    if (el && last !== null && el.scrollTop < last - 10) return;
    if (ts - lastScrollTsRef.current < 50) return;
    lastScrollTsRef.current = ts;
    resultScrollRef.current?.scrollIntoView({ block: 'end' });
    lastAutoScrollTopRef.current = el ? el.scrollTop : null;
  };

  const resetTypewriter = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    typewriterQueueRef.current = [];
    rafRef.current = null;
    lastTsRef.current = null;
    charAccumulatorRef.current = 0;
    currentRateRef.current = BASE_RATE;
    lastScrollTsRef.current = 0;
    lastCommitTsRef.current = 0;
    pendingChangedBlocksRef.current.clear();
    pendingResultDeltaRef.current = '';
    lastAutoScrollTopRef.current = null;
  };

  // add near your other refs
  const isHiddenRef = useRef(typeof document !== 'undefined' ? document.hidden : false);

  useEffect(() => {
    const handleVisibility = () => {
      isHiddenRef.current = document.hidden;
      if (!document.hidden) {
        flushBacklogInstantly();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
    //eslint-disable-next-line
  }, []);

  const flushBacklogInstantly = () => {
    const queue = typewriterQueueRef.current;
    if (queue.length === 0) return;

    let resultDelta = '';
    const changedBlocks = new Set<number>();

    for (const item of queue) {
      if (item.blockIndex < 0 || item.blockIndex >= currentResponseBlocks.length) continue;

      const remaining = item.text.slice(item.index);
      if (!remaining) continue;

      const oldBlock = currentResponseBlocks[item.blockIndex];
      currentResponseBlocks[item.blockIndex] = {
        ...oldBlock,
        content: oldBlock.content + remaining,
        ...(item.endsBlock ? { endedAt: performance.now() } : {}),
      };
      changedBlocks.add(item.blockIndex);

      if (item.isVisibleText) resultDelta += remaining;
      item.index = item.text.length;
    }

    typewriterQueueRef.current = [];
    charAccumulatorRef.current = 0;

    if (changedBlocks.size > 0) setResponseBlocks([...currentResponseBlocks]);
    if (resultDelta) setResult((prev) => prev + resultDelta);

    if (networkDoneRef.current) {
      setIsStreamCompleted(true);
      setIsStreamingRequested?.(false);
    }
  };

  const enqueueText = (
    block: ResponseBlock,
    text: string,
    isVisibleText: boolean,
    atomic = false,
    endsBlock = false
  ) => {
    if (!text) return;

    const blockIndex = currentResponseBlocks.indexOf(block);
    if (blockIndex === -1) return;

    if (isHiddenRef.current) {
      flushBacklogInstantly();
      const oldBlock = currentResponseBlocks[blockIndex];
      currentResponseBlocks[blockIndex] = {
        ...oldBlock,
        content: oldBlock.content + text,
        ...(endsBlock ? { endedAt: performance.now() } : {}),
      };
      setResponseBlocks([...currentResponseBlocks]);
      if (isVisibleText) setResult((prev) => prev + text);
      return;
    }

    const queue = typewriterQueueRef.current;
    const last = queue[queue.length - 1];

    if (
      !atomic &&
      last &&
      !last.atomic &&
      last.blockIndex === blockIndex &&
      last.isVisibleText === isVisibleText &&
      last.index === 0
    ) {
      last.text += text;
    } else {
      queue.push({ blockIndex, text, index: 0, isVisibleText, atomic, endsBlock });
    }

    if (rafRef.current === null) {
      lastTsRef.current = null;
      rafRef.current = requestAnimationFrame(tick);
    }
  };

  const tick = (ts: number) => {
    const queue = typewriterQueueRef.current;

    if (queue.length === 0) {
      if (pendingChangedBlocksRef.current.size > 0) {
        setResponseBlocks([...currentResponseBlocks]);
        pendingChangedBlocksRef.current.clear();
      }
      if (pendingResultDeltaRef.current.length > 0) {
        const resultDelta = pendingResultDeltaRef.current;
        pendingResultDeltaRef.current = '';
        setResult((prev) => (prev ?? '') + resultDelta);
      }

      rafRef.current = null;
      lastTsRef.current = null;
      charAccumulatorRef.current = 0;

      if (networkDoneRef.current) {
        setIsStreamCompleted(true);
        setIsStreamingRequested?.(false);
      }

      return;
    }

    if (isHiddenRef.current) {
      pendingChangedBlocksRef.current.clear();
      pendingResultDeltaRef.current = '';

      flushBacklogInstantly();
      rafRef.current = null;
      lastTsRef.current = null;
      charAccumulatorRef.current = 0;
      return;
    }

    if (lastTsRef.current === null) {
      lastTsRef.current = ts;
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    const dt = Math.min((ts - lastTsRef.current) / 1000, 0.1);
    lastTsRef.current = ts;

    const backlog = getBacklog();
    const targetRate = getTargetRate(backlog, networkDoneRef.current);
    const smoothing = 1 - Math.exp(-dt / TIME_CONSTANT);
    currentRateRef.current += (targetRate - currentRateRef.current) * smoothing;

    charAccumulatorRef.current += currentRateRef.current * dt;

    const charsToReveal = Math.floor(charAccumulatorRef.current);

    if (charsToReveal <= 0) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    charAccumulatorRef.current -= charsToReveal;

    let remainingToReveal = charsToReveal;

    while (queue.length > 0 && (remainingToReveal > 0 || queue[0].atomic)) {
      const item = queue[0];

      if (item.blockIndex < 0 || item.blockIndex >= currentResponseBlocks.length) {
        queue.shift();
        continue;
      }

      const remaining = item.text.length - item.index;
      if (remaining <= 0) {
        queue.shift();
        continue;
      }

      const take = item.atomic ? remaining : Math.min(remainingToReveal, remaining);
      const piece = item.text.slice(item.index, item.index + take);

      const oldBlock = currentResponseBlocks[item.blockIndex];
      currentResponseBlocks[item.blockIndex] = {
        ...oldBlock,
        content: oldBlock.content + piece,
        ...(item.endsBlock ? { endedAt: performance.now() } : {}),
      };

      pendingChangedBlocksRef.current.add(item.blockIndex);
      if (item.isVisibleText) pendingResultDeltaRef.current += piece;

      item.index += take;
      if (!item.atomic) remainingToReveal -= take; // atomic markers cost no typing budget

      if (item.index >= item.text.length) queue.shift();
    }

    const shouldCommit = ts - lastCommitTsRef.current >= COMMIT_INTERVAL_MS || queue.length === 0;

    if (shouldCommit) {
      lastCommitTsRef.current = ts;

      if (pendingChangedBlocksRef.current.size > 0) {
        setResponseBlocks([...currentResponseBlocks]);
        pendingChangedBlocksRef.current.clear();
      }

      if (pendingResultDeltaRef.current.length > 0) {
        const resultDelta = pendingResultDeltaRef.current;
        pendingResultDeltaRef.current = '';
        setResult((prev) => (prev ?? '') + resultDelta);
      }

      autoScroll(ts);
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    return () => {
      resetTypewriter();
    };
  }, []);

  const processStreamingRequest = async () => {
    const url = getStreamingServiceUrl();
    const payload = await getStreamingServicePayload();
    sessionStorage.setItem(localAutoScrollEnabledKey, 'true');
    cancelStreaming = false;
    isStreaming = false;
    controller = new AbortController();
    signal = controller.signal;
    metadataSourceList = [];
    setClearState?.(false);
    networkDoneRef.current = false;
    resetTypewriter();
    currentSectionIndex = 0;
    isInThinkingBlock = false;
    hasSeenTextChunk = false;
    currentToolInput = '';
    setSqlSections([]);
    currentResponseBlocks = [];
    setResponseBlocks([]);
    isElysiaError = false;
    isNewFormatThinking = false;
    hasChartBeenSeen = false;
    lastThinkingClosed = false;
    resetCitations();

    if (appKey === 'image_generator') {
      setImageLoading(true);
    }

    try {
      useElysiaStore.setState({ isStreamingStopped: false });
      setIsStreamingRequested?.(true);
      const response = await authenticatedFetch(url, {
        method: 'POST',
        signal: signal,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (newChat && !isPrivateChat && !isPreview && !isAppPreview) {
        const chat = {
          chat_history_title: 'New Chat',
          is_archived: null,
          session_id: String(chatSessionId),
          updated_on: null,
          is_new_chat: true,
        };
        addChatToToday(chat);
        setBackToChatSession?.(false);
      }

      const reader = response?.body?.getReader();
      const decoder = new TextDecoder('utf-8');

      // 1. Initialize buffer for partial lines
      let buffer = '';

      reader?.read().then(function processResult(res) {
        isStreaming = true;
        setDynamicStreamCount?.((prev) => prev + 1);

        //closing thinking block manually for sales app
        const closeNewFormatThinkingBlockIfOpen = () => {
          if (isInThinkingBlock && isNewFormatThinking) {
            const lastBlock = currentResponseBlocks[currentResponseBlocks.length - 1];

            if (lastBlock && lastBlock.type === 'thinking') {
              enqueueText(lastBlock, '\nblock-end\n', false, true, true);
            }

            isInThinkingBlock = false;
            isNewFormatThinking = false;
            lastThinkingClosed = true;
          }
        };

        // Cancel the streaming instead of abort if streaming is already started
        if (cancelStreaming) {
          const lastBlock = currentResponseBlocks[currentResponseBlocks.length - 1];

          if (lastBlock?.type === 'thinking' && lastBlock.endedAt == null) {
            lastBlock.endedAt = performance.now();
            setResponseBlocks([...currentResponseBlocks]);
          }
          reader?.cancel();
          resetTypewriter();

          networkDoneRef.current = false;

          // reseting tool call
          setToolCall('');
          localStorage.setItem(toolCallKey, '');

          setIsStreamingRequested?.(false);

          if (newChat) {
            updateChatHistoryOnDelete(String(chatSessionId));
          }
          if (appKey === 'image_generator') {
            setToolCall('');
            localStorage.setItem(toolCallKey, '');
          }

          return;
        }

        if (res.done) {
          closeNewFormatThinkingBlockIfOpen();
          networkDoneRef.current = true;
          if (!rafRef.current) {
            setIsStreamCompleted(true);
            setIsStreamingRequested?.(false);
          }

          return;
        }

        const chunk = decoder.decode(res.value, { stream: true });
        const combined = buffer + chunk;

        // Spliting whole chunk into tokens
        const tokens = combined.split('\n\n');

        buffer = tokens.pop() || '';

        let ansTokenCat: string = '';
        let metaToken: any = {};

        tokens.forEach((eventBlock) => {
          if (!eventBlock.trim()) return;

          let event = '';

          eventBlock.split('\n').forEach((line) => {
            if (!line.trim()) return;

            if (line.startsWith('event:')) {
              event = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              const jsonData = line.substring(5);

              let pLine;
              try {
                pLine = JSON.parse(jsonData || '{}');
              } catch (e) {
                console.error('JSON Parse Error (skipping line):', e, 'Line:', line);
                return;
              }

              const getLastBlock = () => currentResponseBlocks[currentResponseBlocks.length - 1];

              if (event === 'text_chunk') {
                closeNewFormatThinkingBlockIfOpen();
                if (!hasSeenTextChunk) {
                  hasSeenTextChunk = true;
                }

                const lastBlock = getLastBlock();
                let targetBlock;
                if (lastBlock && lastBlock.type === 'text') {
                  targetBlock = lastBlock;
                } else {
                  targetBlock = { type: 'text', content: '' };
                  currentResponseBlocks.push(targetBlock);
                }
                enqueueText(targetBlock, pLine?.answer || '', true);
              } else if (event === 'content_block_start' && pLine?.type === 'thinking') {
                isInThinkingBlock = true;

                const lastBlock = getLastBlock();
                if (lastBlock && lastBlock.type === 'thinking') {
                  lastBlock.content += '\n\n===SECTION_BREAK===\n\n';
                  lastBlock.content += 'block-start\n';
                  if (lastBlock.startedAt == null) {
                    lastBlock.startedAt = performance.now();
                  }
                } else {
                  currentResponseBlocks.push({
                    type: 'thinking',
                    content: 'block-start\n',
                    startedAt: performance.now(),
                  });
                }

                if (hasSeenTextChunk) {
                  currentSectionIndex++;
                  hasSeenTextChunk = false;
                }

                if (sessionStorage.getItem(localAutoScrollEnabledKey) === 'true') {
                  resultScrollRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
                }
              }
              // code changes for sales app
              else if (event === 'thinking' && !hasChartBeenSeen) {
                if (pLine?.answer !== undefined) {
                  // NEW FORMAT
                  if (!isInThinkingBlock) {
                    isInThinkingBlock = true;
                    isNewFormatThinking = true;
                    if (hasSeenTextChunk) {
                      currentSectionIndex++;
                      hasSeenTextChunk = false;
                    }
                    const lastBlock = getLastBlock();
                    if (lastBlock && lastBlock.type === 'thinking' && !lastThinkingClosed) {
                      enqueueText(
                        lastBlock,
                        '\n\n===SECTION_BREAK===\n\nblock-start\n',
                        false,
                        true
                      );
                    } else {
                      currentResponseBlocks.push({
                        type: 'thinking',
                        content: 'block-start\n',
                        startedAt: performance.now(),
                      });
                      lastThinkingClosed = false;
                    }
                  }
                  enqueueText(getLastBlock(), pLine.answer, false);
                } else if (pLine?.query && isInThinkingBlock) {
                  const lastBlock = getLastBlock();
                  if (lastBlock && lastBlock.type === 'thinking') {
                    enqueueText(lastBlock, '\nblock-end\n', false, true, true);
                    lastThinkingClosed = true;
                  } else {
                    currentResponseBlocks.push({
                      type: 'thinking',
                      content: pLine.query,
                      startedAt: performance.now(),
                    });
                  }
                }
                if (sessionStorage.getItem(localAutoScrollEnabledKey) === 'true') {
                  resultScrollRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
                }
              }
              // Old Flow
              else if (event === 'content_block_end' && pLine?.type === 'thinking') {
                isInThinkingBlock = false;

                const lastBlock = getLastBlock();
                if (lastBlock && lastBlock.type === 'thinking') {
                  lastBlock.endedAt = performance.now();
                  enqueueText(lastBlock, '\nblock-end\n', false, true);
                  lastThinkingClosed = true;
                }
              } else if (event === 'content_block_start' && pLine?.type === 'tool_call') {
                currentToolInput = '';
              } else if (event === 'tool_input' && pLine?.args_delta) {
                currentToolInput += pLine.args_delta;
              } else if (event === 'tool_output' && pLine?.result) {
                try {
                  let purpose = '';
                  let sqlQuery = '';

                  try {
                    const parsedInput = JSON.parse(currentToolInput);
                    purpose = parsedInput.purpose || '';
                    sqlQuery = parsedInput.query || '';
                  } catch (parseError) {
                    const purposeMatch = currentToolInput.match(/"purpose":\s*"([^"]*)"/);
                    const queryMatch = currentToolInput.match(/"query":\s*"((?:[^"\\]|\\.)*)"/);

                    purpose = purposeMatch ? purposeMatch[1] : '';
                    sqlQuery = queryMatch ? queryMatch[1] : '';
                    console.error(parseError);
                  }

                  sqlQuery = sqlQuery
                    .replace(/\\n/g, '\n')
                    .replace(/\\"/g, '"')
                    .replace(/\\\\/g, '\\');

                  const resultData = JSON.parse(pLine.result);
                  const formattedResult = JSON.stringify(resultData, null, 2);

                  setSqlSections((prev) => {
                    const sections = [...prev];

                    let section = sections.find((s) => s.sectionIndex === currentSectionIndex);

                    if (!section) {
                      section = {
                        sectionIndex: currentSectionIndex,
                        intermediate: [],
                        final: [],
                      };
                      sections.push(section);
                    }

                    const step = {
                      label: `Step ${(purpose === 'intermediate' ? section.intermediate.length : section.final.length) + 1}`,
                      code: sqlQuery,
                      result: formattedResult,
                    };

                    if (purpose === 'intermediate') {
                      section.intermediate.push(step);
                    } else if (purpose === 'final') {
                      section.final.push(step);
                    }
                    return sections;
                  });
                } catch (e) {
                  console.error('Error parsing tool result:', e);
                }
              } else if (event === 'citation') {
                const markers = registerCitationEvent(pLine);
                if (markers) {
                  const lastBlock = getLastBlock();
                  let target;
                  if (lastBlock && lastBlock.type === 'text') {
                    target = lastBlock;
                  } else {
                    target = { type: 'text', content: '' };
                    currentResponseBlocks.push(target);
                  }
                  enqueueText(target, markers, false, true);
                }
              } else if (event === 'error') {
                setImageLoading(false);
                isElysiaError = true;
                const errorMessage = pLine?.message ?? pLine?.answer ?? '';
                ansTokenCat += errorMessage;
              } else if (event === 'chartjs') {
                const newChartConfig = {
                  ...pLine.chartjs,
                  _id: Date.now() + Math.random().toString(36).substring(2, 8),
                };
                setChartConfig(newChartConfig);
                hasChartBeenSeen = true;
                currentResponseBlocks.push({
                  type: 'chartjs',
                  content: '',
                });
              } else if (event === 'ppt') {
                setPptConfig(pLine?.answer);
              } else if (event === 'document') {
                setPptConfig(pLine?.answer);
              } else if (event === 'refresh_info') {
                const lastBlock = getLastBlock();
                lastBlock.content += pLine?.answer;
              } else if (event === 'metadata') {
                metaToken = pLine?.metadata;
              } else if (
                event === 'final_response' &&
                ansTokenCat === '' &&
                !hasSeenTextChunk &&
                !isImageGenChat
              ) {
                closeNewFormatThinkingBlockIfOpen();
                ansTokenCat += pLine?.answer;
                isElysiaError = true;
              } else if (event === 'image') {
                isImageGenChat = true;
                setResponseImageId(pLine?.image);
              } else if (event === 'tool_results') {
                const toolRes = [
                  { toolcall: true, searching: localStorage.getItem(toolCallKey) },
                  ...pLine?.tool_results,
                ];

                setToolResults(toolRes);
                localStorage.setItem(localToolResultKey, JSON.stringify(toolRes));
              } else if (event === 'tool_call' || event === 'intermediate_step') {
                if (pLine?.args?.query) {
                  setToolCall(pLine?.args?.query);
                  localStorage.setItem(toolCallKey, pLine?.args?.query);
                }
              }
            }
          });
        });

        if (currentResponseBlocks.length > 0) {
          setResponseBlocks([...currentResponseBlocks]);
        }

        if (ansTokenCat) {
          setResult((prev) => prev + ansTokenCat);

          if (ansTokenCat.includes(ELYSIA_INTERNAL_ERROR_MESSAGE)) {
            isElysiaError = true;
            if (newChat) {
              updateChatHistoryOnDelete(String(chatSessionId));
            }
          } else if (ansTokenCat.includes(ELYSIA_GUARDRAIL_ERROR_MESSAGE)) {
            if (newChat) {
              updateChatHistoryOnDelete(String(chatSessionId));
            }
          }
        }
        if (metaToken && Object.keys(metaToken).length) {
          try {
            metadataSourceList = metaToken?.references;
            setRunId(metaToken?.run_id);
            if (metaToken?.image_monthly_quota) {
              setAvailableImageQuota(metaToken?.image_monthly_quota_remaining);
            }
            if (metaToken?.agent_metadata?.image_generation_agent?.image_model_name) {
              modelUsedForResponse.current = {
                display_name: metaToken?.agent_metadata?.image_generation_agent?.image_model_name,
              };
              languageUsedForResponse.current = null;
            }

            const title = metaToken?.title;
            if (
              !cancelStreaming &&
              title &&
              newChat &&
              isStreamingStopped !== true &&
              !isPrivateChat
            ) {
              updateSessionTitle(String(chatSessionId), title);
              setIsNewSession?.(true);
              setSessionPath?.(`/session/${chatSessionId}`);
              if (window.location.pathname === '/') {
                navigate(`/session/${chatSessionId}`, { replace: true });
              }
              setClearState?.(false);
              setNewChat?.(false);
            }
          } catch (e) {
            isElysiaError = true;
            console.error(e);
          }
        }

        return reader?.read().then(processResult);
      });
    } catch (e: any) {
      isElysiaError = true;
      console.error('error', e);
      setIsStreamingRequested?.(false);
      appendMesgToChatHistory(ELYSIA_INTERNAL_ERROR_MESSAGE, '', '[]', '');
    }
  };

  const processLeadProfileRequest = async () => {
    try {
      const response = await getLeadProfile(prompt);
      appendMesgToChatHistory(response, '', '[]', '');
      return;
    } catch (err) {
      console.error('error fetching profile', err);
    }
  };

  const processPrompt = async () => {
    if (prompt !== '') {
      setIsLoading(true);
      setResult('');
      setPendingMessageFiles?.([]);

      if (chatAgent === 'Lead') {
        processLeadProfileRequest();
      } else {
        if (chatAgent === 'Document') {
          setToolCall(chatAgent);
          localStorage.setItem(toolCallKey, chatAgent);
        }
        if (brandDoc) {
          setToolCall('Document');
          localStorage.setItem(toolCallKey, 'Document');
        }
        processStreamingRequest();
      }
    } else {
      alert('Please enter a prompt...');
    }
  };

  const getStreamingServiceUrl = () => {
    return `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/chat/stream/completion`;
  };

  const getStreamingServicePayload = async () => {
    const valPrompt = prompt ? await encryptAesWithSha256(prompt, profileInfo?.userId) : '';
    const payload = {
      appId:
        appKey === 'finance'
          ? `${import.meta.env.VITE_FINANCE_APP_ID}`
          : `${import.meta.env.VITE_ELYSIA_APP_ID}`,
      query: valPrompt,
      model: selectedModel.model,
      tokens: 40960,
      creativity: creativity,
      personality: personality,
      role: role,
      writing_style: writingStyle,
      domain_expertise: domainExpertise,
      chat_session: chatSessionId,
      private_chat: isPrivateChat,
      system_prompt: '',
      concepts: [],
      entities: [],
      business_units: [],
      products: [],
      content_domains: [],
      showSourceList: showSourceList,
      include_search: includeSearch,
      include_metadata: true,
      intermediate_steps: true,
      response_language: selectedLanguage,
      default_response_language: defaultLanguage,
      default_name_of_model: defaultModel.name_of_model,
      name_of_model: selectedModel.name_of_model,
      ...(appKey === 'brand_agent' && {
        division: division,
        brand: brand?.name,
        tov_id: brandDetails?.id,
      }),
      ...(appKey === 'brand_agent' &&
        !isPreview && { agent_id: brandDetails?.id, persona_id: persona.id }),
      ...(chatAgent === 'app-agent' && !isAppPreview && { agent_id: aiAppId }),
      ...(isPreview && { preview_brand_config: privewApiPayload }),
      ...(isAppPreview && { preview_agent_config: appPreviewApiPayload }),
      ...(appKey == 'image_generator' && {
        image_generation_agent_config: imageGenPayload,
      }),
      ...(appKey == 'finance' && {
        metadata: {
          context: await encryptAesWithSha256(
            JSON.stringify({
              profit_center: { id: profitCenter?.label, level: profitCenter?.level },
              location: { id: financeAppLocation?.label, level: financeAppLocation?.level },
              output_language: selectedLanguage,
            }),
            profileInfo?.userId
          ),
        },
      }),
    };

    if (chatAgent === 'Document' || brandDoc) {
      if (context && context !== 'myContentsOnly') {
        payload['context'] = context;
        payload['filterMyContentsOnly'] = false;
        if (sources && sources.length > 0) {
          payload['sources'] = sources;
        }
      }
      if (context && context === 'myContentsOnly') {
        payload['filterMyContentsOnly'] = true;
        if (sources && sources.length > 0) {
          payload['sources'] = sources;
        }
      }
      payload.showSourceList = true;
      payload['sources_with_prompt'] = pendingMessageFiles;
    }
    return payload;
  };

  const buildSqlSectionsFromHistory = (historyBlocks: any[]) => {
    const sectionsMap = new Map<number, any>();

    let localSectionIndex = 0;
    let lastToolInput: any = null;

    for (const block of historyBlocks) {
      if (block.type === 'thinking') {
        localSectionIndex++;
        continue;
      }

      if (block.type === 'tool_input') {
        try {
          const input =
            typeof block.content === 'string'
              ? JSON.parse(block.content)
              : JSON.parse(block.content?.input ?? '{}');

          lastToolInput = {
            purpose: input.purpose ?? 'intermediate',
            query: input.query ?? '',
          };
        } catch {
          lastToolInput = null;
        }
      }

      if (block.type === 'tool_output' && lastToolInput) {
        let result = '';

        try {
          const parsed = JSON.parse(block.content);
          result = JSON.stringify(parsed.results ?? parsed, null, 2);
        } catch {
          result = block.content;
        }

        if (!sectionsMap.has(localSectionIndex)) {
          sectionsMap.set(localSectionIndex, {
            sectionIndex: localSectionIndex,
            intermediate: [],
            final: [],
          });
        }

        const section = sectionsMap.get(localSectionIndex);

        const step = {
          label: `Step ${
            lastToolInput.purpose === 'final'
              ? section.final.length + 1
              : section.intermediate.length + 1
          }`,
          code: lastToolInput.query,
          result,
        };

        if (lastToolInput.purpose === 'final') {
          section.final.push(step);
        } else {
          section.intermediate.push(step);
        }

        lastToolInput = null;
      }
    }

    return Array.from(sectionsMap.values());
  };

  const parseDocOutput = (output: any) => {
    const text = output?.text;
    let source: any = null;

    if (Array.isArray(text)) {
      source = text;
    } else if (typeof text === 'string' && text.trim().startsWith('[')) {
      try {
        source = JSON.parse(text);
      } catch (error) {
        console.error(error);
      }
    }

    const isBlocks =
      Array.isArray(source) &&
      source.some((b) => b && ['text', 'citation', 'thinking'].includes(b.type));

    // plain text : keep the old behaviour for old chat
    if (!isBlocks) {
      return {
        plainText: typeof text === 'string' ? text : '',
        blocks: [] as any[],
        citations: [] as CitationGroup[],
      };
    }

    const rawBlocks = source.map((b: any) => (b && typeof b === 'object' ? { ...b } : b));

    try {
      const { blocks, citations } = extractCitationsFromRawBlocks(rawBlocks);
      return {
        plainText: '',
        blocks: processResponseBlocks(blocks, output),
        citations,
      };
    } catch (error) {
      console.error('Error processing document blocks:', error);
      return {
        plainText: rawBlocks
          .filter((b: any) => b?.type === 'text' && typeof b.content === 'string')
          .map((b: any) => b.content)
          .join('\n\n'),
        blocks: [] as any[],
        citations: [] as CitationGroup[],
      };
    }
  };

  const processResponseBlocks = (rawBlocks: any[], output?: any) => {
    if (!rawBlocks || rawBlocks.length === 0) return [];

    const historyResponseBlock = [...rawBlocks];

    for (let blockIndex = 0; blockIndex < historyResponseBlock.length; blockIndex++) {
      const currentBlock = historyResponseBlock[blockIndex];

      if (currentBlock.type === 'thinking') {
        currentBlock.content = 'block-start\n' + currentBlock.content + '\nblock-end';

        let nextIndex = blockIndex + 1;

        while (nextIndex < historyResponseBlock.length) {
          const nextBlock = historyResponseBlock[nextIndex];

          if (nextBlock.type === 'text') break;

          if (nextBlock.type === 'thinking') {
            currentBlock.content +=
              '\n\n===SECTION_BREAK===\n\n' + 'block-start\n' + nextBlock.content + '\nblock-end';

            historyResponseBlock.splice(nextIndex, 1);
            continue;
          }

          nextIndex++;
        }
      }
    }

    // adding chart in position for sales app
    if (output?.chartjs) {
      const CHART_MARKERS = ['chart_note', 'Chart generated'];
      const chartIndex = historyResponseBlock.findIndex(
        (block) =>
          block.type === 'tool_output' &&
          typeof block.content === 'string' &&
          CHART_MARKERS.some((marker) => block.content.includes(marker))
      );

      const chartBlock = {
        type: 'chartjs',
        content: '',
        chartConfig: { ...output.chartjs, _id: 'history-chart' },
      };

      if (chartIndex !== -1) {
        // marker exists, keep the chart where it appeared
        historyResponseBlock.splice(chartIndex, 1, chartBlock);
      } else {
        // no marker, put the chart at the top
        historyResponseBlock.unshift(chartBlock);
      }
    }

    return historyResponseBlock;
  };

  return (
    <>
      {/* History List Virtulized */}
      {activeChatHistoryConversation && activeChatHistoryConversation.length > 0 && (
        <Virtuoso<ChatHistoryConversation>
          ref={virtuosoRef}
          style={{ width: '100%' }}
          data={activeChatHistoryConversation}
          overscan={{ main: 500, reverse: 500 }}
          customScrollParent={contentRef.current ?? undefined}
          initialTopMostItemIndex={activeChatHistoryConversation.length - 1}
          computeItemKey={(index, record) => record?.tracking_id ?? index}
          itemContent={(index, record) => {
            let toolResult: ChatHistoryConversation['intermediate_steps'] = [];
            if (record?.intermediate_steps?.length > 1) {
              toolResult = [
                { toolcall: true, searching: record?.intermediate_steps[0]?.args?.query },
                // @ts-expect-error // fix it later
                ...(record?.intermediate_steps[1]?.tool_results || []),
              ];
            }

            let userInput = '';
            let systemResponse = '';
            let systemImageId: string | null = null;
            let chartConfig: any = null;
            let pptConfigObj: PresentationConfig | null = null;
            let historyResponseBlock: any[] = [];
            let historySqlSections: any[] = [];
            let historyCitations: CitationGroup[] = [];

            if (typeof record.input === 'string' && typeof record.output === 'string') {
              userInput = record.input;
              systemResponse = record.output;
            } else {
              const input = record.input?.[0];
              const output = record.output?.[0];

              if (input) {
                userInput = input.text || '';
              }

              if (output) {
                if (output.type === 'text') {
                  systemResponse = output.text || '';
                }
                if (output.type === 'image') {
                  systemImageId = output.image;
                } else if (output.type === 'ppt') {
                  pptConfigObj = output.ppt;
                  const parsed = parseDocOutput(output);
                  systemResponse = parsed.plainText;
                  historyResponseBlock = parsed.blocks;
                  historyCitations = parsed.citations;
                } else if (output.type === 'document') {
                  pptConfigObj = output.document;
                  const parsed = parseDocOutput(output);
                  systemResponse = parsed.plainText;
                  historyResponseBlock = parsed.blocks;
                  historyCitations = parsed.citations;
                } else if (
                  output.type === 'finance' ||
                  output.type === 'tnf_sales' ||
                  output.type === 'chartjs' ||
                  output.type === 'text'
                ) {
                  chartConfig = output.chartjs;

                  let rawBlocks: any[] | null = null;
                  if (typeof output.text === 'string' && output.text.trim().startsWith('[')) {
                    try {
                      const parsed = JSON.parse(output.text);
                      const looksLikeBlocks =
                        Array.isArray(parsed) &&
                        parsed.some(
                          (b) =>
                            b &&
                            (b.type === 'text' || b.type === 'citation' || b.type === 'thinking')
                        );
                      if (looksLikeBlocks) {
                        rawBlocks = parsed;
                      }
                    } catch (error) {
                      rawBlocks = null;
                      console.error(error);
                    }
                  }

                  if (rawBlocks) {
                    try {
                      const { blocks: blocksWithoutCitations, citations: extractedCitations } =
                        extractCitationsFromRawBlocks(rawBlocks);
                      historyResponseBlock = processResponseBlocks(blocksWithoutCitations, output);
                      historyCitations = extractedCitations;
                      historySqlSections = buildSqlSectionsFromHistory(rawBlocks);
                    } catch (error) {
                      console.error('Error processing response blocks:', error);
                      historyResponseBlock = [];
                      historySqlSections = [];
                      historyCitations = [];
                    }
                  } else {
                    systemResponse = output.text || '';
                  }
                }
              }
            }

            return (
              <div>
                <ChatMessage
                  message={{
                    body: formatInput(userInput),
                    type: 'User',
                    attachedFiles: record?.sources_with_prompt ?? [],
                  }}
                />
                <ChatMessage
                  message={{
                    body: systemResponse,
                    responseBlocks: historyResponseBlock,
                    sqlSections: historySqlSections,
                    citations: historyCitations,
                    type: 'System',
                    source_used_for_response: record.source_used_for_response,
                    references: record.references,
                    runId: record?.run_id,
                    feedback: record?.feedback,
                    chartConfig,
                    toolResult,
                    ...((record?.model_used_for_response || record?.response_language) &&
                      models.current && {
                        modelUsed:
                          appKey === 'image_generator'
                            ? record?.agent_metadata?.image_generation_agent?.image_model_name
                            : models?.current[
                                record?.model_used_for_response as keyof typeof models.current
                              ],
                        languageUsed:
                          appKey === 'image_generator' ? null : record?.response_language,
                      }),
                    ...(systemImageId != null && { imageId: systemImageId }),
                    ...(pptConfigObj && { pptConfig: pptConfigObj }),
                  }}
                  historySystemMessage
                />
              </div>
            );
          }}
        />
      )}
      {/*Active chat---> For Streaming + Active history*/}
      {isChatActive && combinedMessages.length > 0 && (
        <div>
          {combinedMessages.map((chatMsg, index) => (
            <ChatMessage key={index} message={chatMsg} />
          ))}
          <div ref={resultScrollRef} />
        </div>
      )}
      {/* Screen-reader support */}
      <div className="sr-only" aria-live="polite" role="status">
        {chatMessages.length > 0 && chatMessages[chatMessages.length - 1].type === 'System' && (
          <>
            Elysia Says:{' '}
            {(chatMessages[chatMessages.length - 1]?.toolResult?.length ?? 0) > 0 && (
              <>
                Searched: {(chatMessages[chatMessages.length - 1]?.toolResult?.length ?? 0) - 1}{' '}
                sites,
              </>
            )}
            <>
              <ReactMarkdown
                children={formatMarkdown(chatMessages[chatMessages.length - 1]?.body)}
              />
            </>
          </>
        )}
      </div>
      <div ref={scrollRef} />
    </>
  );
};

export default ChatSession;
